import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PixelService } from '@/services/pixels.service.ts';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast.ts';
import useModalStore from '@/store/useModalStore.ts';
import { isAxiosError } from 'axios';
import { Link } from '@/@types/Pagination';

interface UsePixelsProps {
  productId: string;
}

export function usePixels({ productId }: UsePixelsProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('pixels');
  const { closeModal } = useModalStore();

  const { data: pixels, isLoading } = useQuery({
    queryKey: ['pixels', productId],
    queryFn: () => PixelService.listPixels(productId),
  });

  const createPixelMutation = useMutation({
    mutationFn: (data: any) => PixelService.createPixel({ productId, data }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [`pixels-${productId}`] });

      const previousQueries = queryClient.getQueriesData({
        queryKey: [`pixels-${productId}`],
      });

      return { previousQueries };
    },
    onSuccess: (serverPixel: any, variables, context) => {
      if (serverPixel && context?.previousQueries) {
        queryClient.setQueriesData(
          { queryKey: [`pixels-${productId}`], exact: false },
          (oldData: any) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              data: [serverPixel.data, ...oldData.data],
            };
          }
        );
      }

      queryClient.invalidateQueries({ queryKey: ['pixels', productId] });

      toast({
        description: t('pixel_created_description'),
        variant: 'success',
      });
      closeModal('add-pixel');
    },
    onError: (error) => {
      if (isAxiosError(error)) {
        toast({
          description:
            error?.response?.data?.message ||
            t('pixel_create_error_description'),
          variant: 'error',
        });
      } else {
        toast({
          description: t('pixel_create_error_description'),
          variant: 'error',
        });
      }
    },
  });

  const updatePixelMutation = useMutation({
    mutationFn: async ({ pixelId, data }: { pixelId: string; data: any }) =>
      PixelService.updatePixel({ productId, pixelId, data }),
    onMutate: async (updatedData) => {
      await queryClient.cancelQueries({
        queryKey: [`pixels-${updatedData.pixelId}`],
      });

      const previousQueries = queryClient.getQueriesData({
        queryKey: [`pixels-${updatedData.pixelId}`],
      });

      const optimisticPixel: Link = {
        ...pixels!,
        ...updatedData,
      };

      queryClient.setQueriesData(
        { queryKey: [`pixels-${updatedData.pixelId}`], exact: false },
        (oldData: any) => {
          if (!oldData || !pixels?.id) return oldData;

          return {
            ...oldData,
            data: oldData.data.map((l) =>
              l.id === pixels.id ? optimisticPixel : l
            ),
          };
        }
      );

      return { previousQueries, optimisticPixel };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pixels', productId] });
      toast({
        description: t('pixel_updated_description'),
        variant: 'success',
      });
      closeModal('edit-pixel');
    },
    onError: (error) => {
      console.error('Mutation error:', error);

      // Surge a mensagem específica do backend quando existir (ex.: "Configure ao menos
      // uma Conversion Action antes de ativar este pixel") — sem isso o produtor só via um
      // erro genérico e não entendia por que o toggle "Ativo" não salvou.
      const fieldErrors = isAxiosError(error)
        ? Object.values(error.response?.data?.errors ?? {})[0]
        : undefined;
      const description =
        (Array.isArray(fieldErrors) ? fieldErrors[0] : undefined) ??
        (isAxiosError(error) ? error.response?.data?.message : undefined) ??
        t('pixel_update_error_description');

      toast({ description, variant: 'error' });
    },
  });

  const deletePixelMutation = useMutation({
    mutationFn: (pixelId: string) =>
      PixelService.deletePixel({ productId, pixelId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pixels', productId] });
      toast({
        description: t('pixel_deleted_description'),
        variant: 'success',
      });
      closeModal('delete-pixel');
    },
    onError: () => {
      toast({
        description: t('pixel_delete_error_description'),
        variant: 'error',
      });
    },
  });

  return {
    pixels,
    isLoading,
    isPending:
      createPixelMutation.isPending ||
      updatePixelMutation.isPending ||
      deletePixelMutation.isPending,
    createPixel: createPixelMutation.mutate,
    updatePixel: updatePixelMutation.mutate,
    deletePixel: deletePixelMutation.mutate,
    isCreating: createPixelMutation.isPending,
    isUpdating: updatePixelMutation.isPending,
    isDeleting: deletePixelMutation.isPending,
  };
}
